import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import features from 'modules/core/store/collections/features'
import app, { FlashType } from 'modules/core/store/models/app'

import Item from 'modules/bookkeepers/components/IncidenceOptionsSection/_Item/landingPageItem'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useActivateInhousePayroll } from 'modules/payroll_bundle/hooks/useActivateInhousePayroll'
import {
  SECTION_NAMES,
  useIncidenceOptionSections,
} from 'modules/payroll_bundle/hooks/useIncidenceOptionSections'

import LandingAnimation from './components/LandingAnimation'

const InhouseActivationPage = () => {
  const { activateInhousePayroll } = useActivateInhousePayroll()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { incidenceOptionSections } = useIncidenceOptionSections(companyId, false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [buttonClickCounter, setCounter] = React.useState(0)
  const hasInhousePayroll = hasFeature('inhouse_payroll')
  const hasDevCompensations = hasFeature('dev_compensations')

  const defaultSection = incidenceOptionSections.find(
    (section: { id: string }) => section.id === SECTION_NAMES.default_updates
  )
  const defaultOptionList = defaultSection?.incidenceOptions || []

  const initialOptions = defaultOptionList.reduce(
    (acc, option) => ({ ...acc, [option.id]: true }),
    {}
  )

  const [activeOptions, setActiveOptions] = React.useState<{ [key: string]: boolean }>(
    initialOptions
  )

  const handleToggleChange = (id: string) => {
    setActiveOptions((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }))
  }

  const showFlash = (type: FlashType, message: string) => {
    app.setFlash({ type, message })
  }

  const { running: loading, performAction: handleClick } = useAsyncAction(async () => {
    const result = await activateInhousePayroll(companyId, activeOptionNames)

    const handled = handleResult(result, () => {
      showFlash('wrong', i18n.t('bookkeepers.inhouse_activation_page.error'))
      return
    })

    if (handled.success) {
      setCounter(buttonClickCounter + 1)
      await features.fetch()
      track(trackingEvents.payroll.userClicksOnStartNow)
    }
  })

  useEffect(() => {
    if (!loading && hasInhousePayroll) {
      navigate({
        pathname: hasDevCompensations
          ? navigation.compensations.employeeUpdates.fullPath()
          : navigation.payrollBundle.incidences.fullPath(),
        search: buttonClickCounter > 0 ? '?activated=true' : '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasInhousePayroll, buttonClickCounter])

  const allInactive =
    activeOptions && Object.values(activeOptions).every((value) => value === false)
  const activeOptionNames = Object.keys(activeOptions).filter((key) => activeOptions[key] === true)

  return (
    <ResponsivePage.Body variant="narrow" showHeader={false}>
      <Box alignItems="center">
        <Box paddingBottom="s48" alignItems="center" width="full">
          <LandingAnimation />
        </Box>
        <Box
          textAlign="center"
          gap="s12"
          paddingBottom="s48"
          style={{ maxWidth: '80%' }}
          width="full"
        >
          <Text.Header1>{i18n.t('bookkeepers.inhouse_activation_page.title')}</Text.Header1>
          <Text size="300">{i18n.t('bookkeepers.inhouse_activation_page.description')}</Text>
        </Box>
        <Box alignItems="center" textAlign="center" paddingBottom="s48" gap="s12" width="full">
          <Box gap="s12">
            {defaultOptionList.map((incidenceOption) => (
              <Item
                key={incidenceOption.id + incidenceOption.name}
                title={incidenceOption.getTranslatedTitle()}
                description=""
                onTogglerChange={() => handleToggleChange(incidenceOption.id)}
                disabled={false}
                active={activeOptions[incidenceOption.id] || false}
                onSetValidation={() => {
                  throw new Error('This function should never be called')
                }}
                needsValidation={false}
                isSettings
              />
            ))}
          </Box>

          <Box>{i18n.t('bookkeepers.inhouse_activation_page.description_1')}</Box>
        </Box>

        <Box alignItems="center" width="full">
          <Button
            aria-label={i18n.t('bookkeepers.inhouse_activation_page.button')}
            type="primary"
            disabled={allInactive || loading}
            onClick={handleClick}
          >
            {i18n.t('bookkeepers.inhouse_activation_page.button')}
          </Button>
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default InhouseActivationPage
